<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'

import { useBackgroundParallax } from '@/composables/useBackgroundParallax'
import type { SupabaseChattinessWarning } from '@/dev/supabaseRequestTrace'
import { useAppearanceSession } from '@/features/appearance/useAppearanceSession'
import { useBotsSession } from '@/features/bots/useBotsSession'
import { useAuthStore } from '@/features/account/session'
import { usePersonasSession } from '@/features/personas/usePersonasSession'
import { useMyProfileStore } from '@/features/account/profile'
import { useAccountSession } from '@/features/account/useAccountSession'
import { isPublicRoute } from '@/router/access'
import { useCatalogSessions } from '@/stores/useCatalogSessions'

const { appearanceLoading } = useAppearanceSession()
useBotsSession()
const auth = useAuthStore()
usePersonasSession()
useAccountSession()
useCatalogSessions()
const myProfileStore = useMyProfileStore()
const route = useRoute()
const router = useRouter()
const apiWarning = ref<SupabaseChattinessWarning | null>(null)
useBackgroundParallax()
const BACKGROUND_ATTR = 'data-app-background'
type AppBackgroundMode =
  | 'none'
  | 'ambient-gradient'
  | 'library-gradient'
  | 'profile-gradient'
  | 'story-gradient'
  | 'editor-gradient'
  | 'auth-gradient'

const syncBackground = (value?: AppBackgroundMode | null): void => {
  if (value && value !== 'none') {
    document.body.setAttribute(BACKGROUND_ATTR, value)
    return
  }
  document.body.removeAttribute(BACKGROUND_ATTR)
}

function handleApiChattiness(event: Event): void {
  apiWarning.value = (event as CustomEvent<SupabaseChattinessWarning>).detail
}

function dismissApiWarning(): void {
  apiWarning.value = null
}

function downloadApiTrace(): void {
  window.storytellerSupabaseTrace?.download()
}

// Also covers retrying an unknown initial session and confirming there is none.
watch([() => auth.status, () => route.fullPath], ([status]) => {
  if (status === 'anonymous' && !isPublicRoute(route.name)) {
    void router.replace({ name: 'login', query: { redirect: route.fullPath } })
  }
}, { immediate: true })

watch(() => route.meta?.background, (value) => {
  syncBackground(value)
}, { immediate: true })

watch(() => route.fullPath, () => {
  apiWarning.value = null
})

onMounted(() => {
  window.addEventListener('storyteller:supabase-chattiness', handleApiChattiness)
})

onBeforeUnmount(() => {
  window.removeEventListener('storyteller:supabase-chattiness', handleApiChattiness)
  document.body.removeAttribute(BACKGROUND_ATTR)
})
</script>

<template>
  <div class="app-bg" aria-hidden="true">
    <div class="app-bg__layer app-bg__layer--1"></div>
    <div class="app-bg__layer app-bg__layer--2"></div>
    <div class="app-bg__layer app-bg__layer--3"></div>
    <div class="app-bg__layer app-bg__layer--4"></div>
  </div>
  <div v-if="auth.status === 'error' && !isPublicRoute(route.name)" class="p-8 text-on-surface" role="alert">
    <p>Your sign-in status could not be checked.</p>
    <button class="underline cursor-pointer" type="button" @click="auth.initialize()">Retry</button>
  </div>
  <p v-else-if="auth.loading && !isPublicRoute(route.name)" class="p-8 text-on-surface" role="status">Checking your sign-in…</p>
  <p v-else-if="auth.status === 'anonymous' && !isPublicRoute(route.name)" class="p-8 text-on-surface" role="status">Redirecting to sign in…</p>
  <p v-else-if="appearanceLoading" class="p-8 text-on-surface" role="status">Loading appearance…</p>
  <RouterView v-else />
  <aside v-if="auth.isAuthenticated && myProfileStore.error" class="account-profile-notice" role="alert">
    <p>Your profile information could not be loaded.</p>
    <button type="button" :disabled="myProfileStore.loading" @click="myProfileStore.fetchMyProfile(true).catch(() => {})">Retry profile</button>
  </aside>
  <aside
    v-if="apiWarning"
    class="api-chattiness-warning"
    role="status"
    aria-live="polite"
  >
    <div class="api-chattiness-warning__copy">
      <strong>High API activity</strong>
      <span>{{ apiWarning.request_count }} Supabase calls while loading this page.</span>
    </div>
    <div class="api-chattiness-warning__actions">
      <button
        type="button"
        class="api-chattiness-warning__button"
        @click="downloadApiTrace"
      >
        Download trace
      </button>
      <button
        type="button"
        class="api-chattiness-warning__button api-chattiness-warning__button--ghost"
        aria-label="Dismiss API activity warning"
        @click="dismissApiWarning"
      >
        Dismiss
      </button>
    </div>
  </aside>
</template>

<style scoped>
.account-profile-notice { position: fixed; z-index: 60; bottom: max(1rem, env(safe-area-inset-bottom)); left: 50%; transform: translateX(-50%); width: min(28rem, calc(100vw - 2rem)); padding: 1rem; border-radius: .5rem; background: var(--theme-surface-solid); color: var(--theme-on-surface); box-shadow: 0 4px 18px #0003; }
.account-profile-notice button { margin-top: .5rem; cursor: pointer; text-decoration: underline; }
.account-profile-notice button:disabled { opacity: .5; cursor: default; }

.api-chattiness-warning {
  position: fixed;
  right: max(16px, env(safe-area-inset-right));
  bottom: max(16px, env(safe-area-inset-bottom));
  z-index: 80;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  max-width: min(520px, calc(100vw - 32px));
  padding: 12px;
  border: 1px solid color-mix(in srgb, var(--theme-warning, #f59e0b) 42%, var(--theme-border, rgba(255, 255, 255, 0.14)));
  border-radius: 8px;
  background: color-mix(in srgb, var(--theme-surface, #151719) 92%, black);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
  color: var(--theme-text, #f8fafc);
}

.api-chattiness-warning__copy {
  display: grid;
  gap: 2px;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.35;
}

.api-chattiness-warning__copy strong {
  color: color-mix(in srgb, var(--theme-warning, #f59e0b) 88%, var(--theme-text, #f8fafc));
  font-size: 0.8rem;
  letter-spacing: 0;
  text-transform: uppercase;
}

.api-chattiness-warning__copy span {
  overflow-wrap: anywhere;
  color: var(--theme-text-muted, #cbd5e1);
}

.api-chattiness-warning__actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.api-chattiness-warning__button {
  min-height: 34px;
  padding: 0 10px;
  border: 1px solid color-mix(in srgb, var(--theme-warning, #f59e0b) 44%, transparent);
  border-radius: 6px;
  background: color-mix(in srgb, var(--theme-warning, #f59e0b) 16%, transparent);
  color: var(--theme-text, #f8fafc);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
}

.api-chattiness-warning__button:hover {
  background: color-mix(in srgb, var(--theme-warning, #f59e0b) 24%, transparent);
}

.api-chattiness-warning__button--ghost {
  border-color: transparent;
  background: transparent;
  color: var(--theme-text-muted, #cbd5e1);
}

.api-chattiness-warning__button--ghost:hover {
  background: color-mix(in srgb, var(--theme-text, #f8fafc) 8%, transparent);
}

@media (max-width: 640px) {
  .api-chattiness-warning {
    left: 16px;
    right: 16px;
    grid-template-columns: 1fr;
  }

  .api-chattiness-warning__actions {
    justify-content: flex-end;
  }
}
</style>
